// FragmentNew — worker → learner(s) fragment composer.
//
// Fields:
//   - 文字    : <textarea>   optional
//   - 照片    : multi-file   optional (max 6)
//   - 影片    : single file  optional (≤300MB, ffmpeg server-side transcode)
//   - 收件人  : radio(全體 / 指定) + learner checkbox list
//
// At least one of (text / photo / video) must be provided.
//
// Write flow (Stage 10: setDoc fires ONLY after all media URLs ready —
// avoids notifyFragmentCreated push landing before the media is visible):
//   1. Pre-mint fragmentId (doc(fragmentsCol()).id) — local only.
//   2. Parallel upload photos + video raw.
//   3. If video: poll transcoded fragments-videos/{fid}.mp4 via
//      getDownloadURL until it returns a URL (exponential backoff
//      2s → 4s → 8s → 16s, max 5 attempts ≈ 30s).
//   4. setDoc with full payload → notifyFragmentCreated CF fires push,
//      learner sees complete fragment immediately.

import {
  Hairline,
  Han,
  HUD,
  MonoLabel,
  T,
  TextBtn,
  BreathDot,
} from "@goodbye/ui";
import { fragmentDoc, fragmentsCol } from "@goodbye/firebase";
import {
  Timestamp,
  doc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAdminAuthCtx, useAdminSyncCtx } from "../context";
import { uploadFragmentPhotos } from "../lib/uploadFragmentPhotos";
import {
  uploadFragmentVideo,
  type UploadFragmentVideoHandle,
} from "../lib/uploadFragmentVideo";
import { pollTranscodedVideo } from "../lib/pollTranscodedVideo";

type Phase = "idle" | "uploading" | "transcoding" | "done" | "error";

const MAX_PHOTOS = 6;

export function FragmentNew() {
  const navigate = useNavigate();
  const { email } = useAdminAuthCtx();
  const { users, fragments } = useAdminSyncCtx();
  // 2026-05-30:把 worker(u15-u23)加回收碎片對象清單(原本濾掉)。下游
  // notifyFragmentCreated 不分 role、worker 也有 fcmToken,功能正常。變數名
  // 沿用 `learners`(全選/map 都吃它),語意現在是「所有可收對象」。
  const learners = useMemo(() => users, [users]);

  // ?from=<fragmentId> — resend retracted
  const [searchParams] = useSearchParams();
  const fromId = searchParams.get("from");
  const sourceFragment = fromId
    ? fragments.find((f) => f.id === fromId)
    : null;

  const [text, setText] = useState("");
  const [broadcast, setBroadcast] = useState(true);
  const [audience, setAudience] = useState<Set<string>>(new Set());

  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  // 2026-05-31: when ≥2 photos are selected, the worker can choose to send
  // them as ONE fragment (album carousel, current behavior) or split into
  // MANY fragments (one photo each). Defaults to "one". Forced back to "one"
  // when a video is attached (splitting one video across N fragments is
  // ambiguous) or when fewer than 2 photos are selected.
  const [photoSplit, setPhotoSplit] = useState<"one" | "many">("one");

  const [videoFile, setVideoFile] = useState<File | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);

  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current) return;
    if (!sourceFragment) return;
    prefilledRef.current = true;
    setText(sourceFragment.text ?? "");
    setBroadcast(sourceFragment.broadcast);
    setAudience(new Set(sourceFragment.audienceUserIds));
  }, [sourceFragment]);

  // Photo preview URLs
  useEffect(() => {
    const urls = photoFiles.map((f) => URL.createObjectURL(f));
    setPhotoPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [photoFiles]);

  const [phase, setPhase] = useState<Phase>("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [doneId, setDoneId] = useState<string | null>(null);
  // How many fragments the last submit created (1 for single/album, N for split).
  const [doneCount, setDoneCount] = useState(1);
  const cancelRef = useRef<UploadFragmentVideoHandle | null>(null);

  function toggleAudience(userId: string) {
    setAudience((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  function addPhotos(files: FileList | null) {
    if (!files) return;
    const next = [...photoFiles];
    for (const f of files) {
      if (next.length >= MAX_PHOTOS) break;
      next.push(f);
    }
    setPhotoFiles(next);
  }

  function removePhoto(idx: number) {
    setPhotoFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  function pickVideo(f: File | null) {
    if (!f) {
      setVideoFile(null);
      return;
    }
    if (f.size > 300 * 1024 * 1024) {
      setError("影片超過 300MB、請先在手機上剪短或選短一點的影片。");
      return;
    }
    setError(null);
    setVideoFile(f);
    // A video can't be meaningfully split across multiple fragments — fall
    // back to single-fragment mode so the「多個碎片」选项 doesn't conflict.
    setPhotoSplit("one");
  }

  async function onSubmit() {
    setError(null);
    const trimmedText = text.trim();
    if (!trimmedText && photoFiles.length === 0 && !videoFile) {
      setError("文字、照片或影片請至少填一個");
      return;
    }
    if (!broadcast && audience.size === 0) {
      setError("請至少選一位收件人或切回全體");
      return;
    }

    // Split into N fragments only when the worker picked "many", there are
    // ≥2 photos, and there's no video (a video can't be split). Otherwise the
    // existing single-fragment path runs (one fragment, all photos + video).
    const splitMany =
      photoSplit === "many" && photoFiles.length >= 2 && !videoFile;

    setPhase("uploading");
    setProgress(0);

    // Shared doc payload — same text / audience / source for every fragment,
    // only photoUrls (and optional video) differ between the one/many paths.
    const baseFields = {
      text: trimmedText,
      broadcast,
      audienceUserIds: broadcast ? [] : Array.from(audience),
      source: "worker" as const,
      createdBy: email ?? "unknown",
    };

    try {
      if (splitMany) {
        // One fragment per photo. Each gets its own id + its own single-photo
        // upload, and the same text/audience. Sequential so progress is
        // monotonic; a mid-loop failure leaves earlier fragments written
        // (same no-rollback behavior as the single-photo path).
        let lastId: string | null = null;
        for (let i = 0; i < photoFiles.length; i++) {
          const id = doc(fragmentsCol()).id;
          const { photoUrls } = await uploadFragmentPhotos({
            fragmentId: id,
            files: [photoFiles[i]],
          });
          await setDoc(fragmentDoc(id), {
            ...baseFields,
            createdAt: serverTimestamp() as unknown as Timestamp,
            photoUrls,
          });
          lastId = id;
          setProgress(Math.round(((i + 1) / photoFiles.length) * 100));
        }
        setDoneCount(photoFiles.length);
        setPhase("done");
        setDoneId(lastId);
        return;
      }

      // Single-fragment path (one fragment holding all photos + optional video).
      const ref = doc(fragmentsCol());
      const fragmentId = ref.id;

      // Parallel uploads. Photo upload is sequential internally but
      // overall it's one Promise; video is a resumable task we keep a
      // handle on for cancellation.
      const photoPromise =
        photoFiles.length > 0
          ? uploadFragmentPhotos({ fragmentId, files: photoFiles })
          : Promise.resolve({ photoUrls: [] as string[] });

      let videoPromise: Promise<{ rawStoragePath: string } | null> =
        Promise.resolve(null);
      if (videoFile) {
        const handle = uploadFragmentVideo({
          fragmentId,
          file: videoFile,
          onProgress: (pct) => setProgress(pct),
        });
        cancelRef.current = handle;
        videoPromise = handle.done;
      }

      const [photoRes, videoRes] = await Promise.all([
        photoPromise,
        videoPromise,
      ]);
      cancelRef.current = null;

      let videoUrl: string | undefined;
      let videoStoragePath: string | undefined;
      if (videoRes) {
        setPhase("transcoding");
        videoUrl = await pollTranscodedVideo(fragmentId);
        videoStoragePath = `fragments-videos/${fragmentId}.mp4`;
      }

      await setDoc(fragmentDoc(fragmentId), {
        ...baseFields,
        createdAt: serverTimestamp() as unknown as Timestamp,
        ...(photoRes.photoUrls.length > 0
          ? { photoUrls: photoRes.photoUrls }
          : {}),
        ...(videoUrl
          ? { videoUrl, videoStoragePath: videoStoragePath! }
          : {}),
      });
      setDoneCount(1);
      setPhase("done");
      setDoneId(fragmentId);
    } catch (err) {
      console.error("[FragmentNew] submit failed", err);
      setError(err instanceof Error ? err.message : "送出失敗");
      setPhase("error");
    }
  }

  function cancelUpload() {
    try {
      cancelRef.current?.task.cancel();
    } catch {
      /* swallow */
    }
    cancelRef.current = null;
    setPhase("idle");
    setProgress(0);
    setError(null);
  }

  if (phase === "done" && doneId) {
    return (
      <div
        style={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          background: T.paper,
          paddingBottom: "calc(140px + env(safe-area-inset-bottom, 0px))",
        }}
      >
        <HUD label="工 作 端" />
        <div
          style={{
            padding: "40px 32px",
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          <Han size={22} color={T.ink900}>
            {doneCount > 1 ? `已送出 ${doneCount} 個碎片` : "已送出"}
          </Han>
          <MonoLabel size={12} color={T.ink500}>
            碎 片 編 號 · {doneId}
          </MonoLabel>
          <Hairline />
          <Han size={14} color={T.ink500} lh={1.9}>
            {doneCount > 1
              ? `${doneCount} 個碎片已寄出。學員手機會收到推播通知。`
              : "碎片已寄出。學員手機會收到推播通知。"}
          </Han>
          <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
            <TextBtn
              size={14}
              color={T.ink900}
              onClick={() => navigate("/fragments")}
              style={{ padding: "10px 14px" }}
            >
              · 回 列 表
            </TextBtn>
          </div>
        </div>
      </div>
    );
  }

  const canSubmit =
    (text.trim() || photoFiles.length > 0 || videoFile) && phase === "idle";

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        background: T.paper,
        paddingBottom: "calc(190px + env(safe-area-inset-bottom, 0px))",
      }}
    >
      <HUD label="工 作 端" />
      <div
        style={{
          padding: "20px 32px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
          }}
        >
          <Han size={22} color={T.ink900}>
            新 碎 片
          </Han>
          <TextBtn
            size={12}
            color={T.ink350}
            framed={false}
            onClick={() => navigate("/fragments")}
          >
            · 返回
          </TextBtn>
        </div>
      </div>
      <Hairline style={{ margin: "8px 32px 0" }} />

      <div
        style={{
          padding: "20px 32px",
          display: "flex",
          flexDirection: "column",
          gap: 18,
          flex: 1,
        }}
      >
        {/* 文字 */}
        <div>
          <MonoLabel
            size={11}
            color={T.ink500}
            style={{ display: "block", marginBottom: 4 }}
          >
            文 字 (選 填)
          </MonoLabel>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="想 對 學 員 說 的 話…"
            rows={6}
            style={{
              width: "100%",
              fontFamily: T.han,
              fontSize: 15,
              lineHeight: 1.7,
              color: T.ink700,
              background: "transparent",
              border: "none",
              borderBottom: `1px solid ${T.ink200}`,
              padding: "8px 0",
              outline: "none",
              resize: "vertical",
            }}
          />
        </div>

        {/* 照片 */}
        <div>
          <MonoLabel
            size={11}
            color={T.ink500}
            style={{ display: "block", marginBottom: 8 }}
          >
            照 片 (選 填、最 多 {MAX_PHOTOS} 張)
          </MonoLabel>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              addPhotos(e.target.files);
              if (e.target) e.target.value = "";
            }}
          />
          {photoPreviews.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 8,
                marginBottom: 10,
              }}
            >
              {photoPreviews.map((src, i) => (
                <div
                  key={i}
                  style={{
                    position: "relative",
                    aspectRatio: "1 / 1",
                    border: `1px solid ${T.ink200}`,
                  }}
                >
                  <img
                    src={src}
                    alt={`照片 ${i + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <button
                    onClick={() => removePhoto(i)}
                    aria-label="移除這張"
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      width: 32,
                      height: 32,
                      background: T.paper,
                      border: `1px solid ${T.ink900}`,
                      padding: 0,
                      fontSize: 19,
                      lineHeight: 1,
                      fontFamily: T.mono,
                      color: T.ink900,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      touchAction: "manipulation",
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          {photoFiles.length < MAX_PHOTOS && (
            <TextBtn
              size={12}
              color={T.ink900}
              ls="0.12em"
              onClick={() => photoInputRef.current?.click()}
              style={{
                padding: "8px 14px",
                border: `1px solid ${T.ink200}`,
                whiteSpace: "nowrap",
              }}
            >
              · 加 照 片
            </TextBtn>
          )}
        </div>

        {/* 2026-05-31: ≥2 photos → let the worker choose one album fragment
            vs N separate fragments. Hidden for 0-1 photos (no meaning). The
            「多個碎片」option is disabled when a video is attached (a single
            video can't be split across fragments — pickVideo also forces
            photoSplit back to "one"). */}
        {photoFiles.length >= 2 && (
          <div>
            <MonoLabel
              size={11}
              color={T.ink500}
              style={{ display: "block", marginBottom: 8 }}
            >
              {photoFiles.length} 張 照 片 怎 麼 送
            </MonoLabel>
            <div style={{ display: "flex", gap: 14, alignItems: "baseline" }}>
              <AudienceRadio
                active={photoSplit === "one"}
                label="一 個 碎 片"
                onClick={() => setPhotoSplit("one")}
              />
              <AudienceRadio
                active={photoSplit === "many"}
                label="多 個 碎 片"
                onClick={() => setPhotoSplit("many")}
                disabled={!!videoFile}
              />
            </div>
            <Han
              size={12}
              color={T.ink350}
              lh={1.6}
              style={{ display: "block", marginTop: 6 }}
            >
              {videoFile
                ? "· 有 影 片 時 只 能 合 成 一 個 碎 片"
                : photoSplit === "one"
                  ? "· 全 部 照 片 放 在 同 一 個 碎 片(可 左 右 滑)"
                  : "· 每 張 照 片 各 自 一 個 碎 片(文 字 都 會 帶 上)"}
            </Han>
          </div>
        )}

        {/* 影片 */}
        <div>
          <MonoLabel
            size={11}
            color={T.ink500}
            style={{ display: "block", marginBottom: 8 }}
          >
            影 片 (選 填、最 大 300MB)
          </MonoLabel>
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            style={{ display: "none" }}
            onChange={(e) => {
              pickVideo(e.target.files?.[0] ?? null);
              if (e.target) e.target.value = "";
            }}
          />
          {videoFile ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                border: `1px solid ${T.ink200}`,
              }}
            >
              <span
                style={{
                  fontFamily: T.mono,
                  fontSize: 18,
                  color: T.ink700,
                }}
                aria-hidden="true"
              >
                ▶
              </span>
              <div
                style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}
              >
                <Han
                  size={13}
                  color={T.ink900}
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {videoFile.name}
                </Han>
                <MonoLabel size={10} color={T.ink500}>
                  {(videoFile.size / 1024 / 1024).toFixed(1)} MB
                </MonoLabel>
              </div>
              <TextBtn
                size={11}
                color={T.ink500}
                ls="0.12em"
                onClick={() => setVideoFile(null)}
                style={{
                  padding: "6px 10px",
                  border: `1px solid ${T.ink200}`,
                  whiteSpace: "nowrap",
                }}
              >
                · 移 除
              </TextBtn>
            </div>
          ) : (
            <TextBtn
              size={12}
              color={T.ink900}
              ls="0.12em"
              onClick={() => videoInputRef.current?.click()}
              style={{
                padding: "8px 14px",
                border: `1px solid ${T.ink200}`,
                whiteSpace: "nowrap",
              }}
            >
              · 加 影 片
            </TextBtn>
          )}
        </div>

        {/* 收件人 */}
        <div>
          <MonoLabel
            size={11}
            color={T.ink500}
            style={{ display: "block", marginBottom: 8 }}
          >
            收 件 人
          </MonoLabel>
          <div
            style={{
              display: "flex",
              gap: 14,
              alignItems: "baseline",
              marginBottom: 8,
            }}
          >
            <AudienceRadio
              active={broadcast}
              label="全 體"
              onClick={() => setBroadcast(true)}
            />
            <AudienceRadio
              active={!broadcast}
              label="指 定"
              onClick={() => setBroadcast(false)}
            />
          </div>
          {!broadcast && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                marginTop: 6,
                maxHeight: "min(280px, 32dvh)",
                overflowY: "auto",
                borderTop: `1px solid ${T.ink100}`,
                borderBottom: `1px solid ${T.ink100}`,
              }}
            >
              {learners.length === 0 && (
                <Han size={13} color={T.ink500} lh={1.9}>
                  找不到學員名單。
                </Han>
              )}
              {learners.map((u) => {
                const checked = audience.has(u.id);
                return (
                  <label
                    key={u.id}
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 12,
                      padding: "10px 0",
                      borderBottom: `1px solid ${T.ink100}`,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAudience(u.id)}
                      style={{ accentColor: T.ink900 }}
                    />
                    <MonoLabel size={11} color={T.ink500}>
                      {u.no}
                    </MonoLabel>
                    <Han size={14} color={T.ink900}>
                      {u.name}
                    </Han>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Sticky submit / progress bar above AdminNav */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: "calc(96px + env(safe-area-inset-bottom, 0px))",
          background: T.paper,
          borderTop: `1px solid ${T.ink100}`,
          padding: "12px 32px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          zIndex: 11,
        }}
      >
        {error && (
          <Han size={13} color={T.ink900} lh={1.7}>
            ⚠ {error}
          </Han>
        )}

        {phase === "idle" && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <TextBtn
              size={14}
              color={canSubmit ? T.ink900 : T.ink350}
              ls="0.12em"
              onClick={onSubmit}
              disabled={!canSubmit}
              style={{
                padding: "10px 18px",
                border: `1px solid ${T.ink200}`,
                whiteSpace: "nowrap",
              }}
            >
              · 送 出
            </TextBtn>
          </div>
        )}

        {phase === "uploading" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <MonoLabel size={11} color={T.ink500}>
              · 上 傳 中 · {Math.round(progress * 100)}%
            </MonoLabel>
            <div
              style={{
                height: 1,
                background: T.ink100,
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: 1,
                  background: T.ink900,
                  width: `${Math.round(progress * 100)}%`,
                  transition: "width 200ms var(--ease-mist)",
                }}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <TextBtn
                size={11}
                color={T.ink500}
                ls="0.12em"
                onClick={cancelUpload}
                style={{
                  padding: "6px 10px",
                  border: `1px solid ${T.ink200}`,
                }}
              >
                · 取 消
              </TextBtn>
            </div>
          </div>
        )}

        {phase === "transcoding" && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 8,
            }}
          >
            <MonoLabel size={11} color={T.ink500}>
              · 處 理 中
            </MonoLabel>
            <Han size={11} color={T.ink350} lh={1.7}>
              影 片 壓 縮 中、約 15-30 秒、 請 不 要 離 開 頁 面
            </Han>
            <BreathDot size={8} ripple />
          </div>
        )}

        {phase === "error" && (
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <TextBtn
              size={13}
              color={T.ink900}
              ls="0.12em"
              onClick={() => {
                setPhase("idle");
                setError(null);
              }}
              style={{
                padding: "8px 14px",
                border: `1px solid ${T.ink900}`,
              }}
            >
              · 重 試
            </TextBtn>
          </div>
        )}
      </div>

    </div>
  );
}

function AudienceRadio({
  active,
  label,
  onClick,
  disabled = false,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        background: "transparent",
        border: "none",
        padding: "10px 0",
        borderBottom: `1px solid ${active ? T.ink900 : T.ink100}`,
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: T.han,
        fontSize: 14,
        color: disabled ? T.ink200 : active ? T.ink900 : T.ink350,
        letterSpacing: "0.18em",
      }}
    >
      {active ? "· " : "  "}
      {label}
    </button>
  );
}
